import Badge from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/utils/copyToClipboard";
import { getIntegrationWithIcon } from "@/utils/integrations";
import { CheckCircle, Copy, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import TruckCircle from "@/assets/icons/TruckCircle";

export interface DeliveryTracking {
  id?: string;
  carrier: string | null;
  carrier_label: string | null;
  tracking_code: string | null;
  tracking_url?: string | null;
  processing_status: string | null;
  processing_status_label: string | null;
  provider: string;
  offer_type?: string | null;
  offer_name?: string | null;
  label?: string | null;
  fulfilled_by?: Array<{
    provider: string;
    external_reference?: string | null;
    at?: string | null;
  }>;
  delivered_at?: string | null;
}

interface TrackingSectionProps {
  tracking?: DeliveryTracking | null;
  trackings?: DeliveryTracking[] | null;
}

function resolveProviderKeys(tracking: DeliveryTracking): string[] {
  const fromFulfilledBy = (tracking.fulfilled_by ?? [])
    .map((entry) => entry.provider)
    .filter(Boolean);

  if (fromFulfilledBy.length > 0) {
    return Array.from(new Set(fromFulfilledBy));
  }

  if (tracking.provider && tracking.provider !== "manual") {
    return [tracking.provider];
  }

  return [];
}

function TrackingCard({
  tracking,
  showHeaderLabel,
}: {
  tracking: DeliveryTracking;
  showHeaderLabel: boolean;
}) {
  const { t } = useTranslation(["orders"]);

  const handleCopyCode = () => {
    if (tracking.tracking_code) {
      copyToClipboard(tracking.tracking_code);
      toast({
        description: t("orders:tracking.copied"),
        variant: "success",
      });
    }
  };

  const isDelivered = tracking.processing_status === "delivered";
  const isError =
    tracking.processing_status === "delivery_failed" ||
    tracking.processing_status === "cancelled_order";

  const renderProviderBadge = () => {
    const providers = resolveProviderKeys(tracking);
    const badges = providers
      .map((provider) => {
        const iconElement = getIntegrationWithIcon(provider);
        if (!iconElement) return null;

        return (
          <div
            key={provider}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-zinc-800/50 border border-zinc-700"
          >
            {iconElement}
          </div>
        );
      })
      .filter(Boolean);

    if (badges.length > 0) {
      return <div className="flex flex-wrap items-center justify-end gap-1.5">{badges}</div>;
    }

    return <Badge status="manual" />;
  };

  const headerLabel =
    tracking.label ||
    [tracking.offer_type, tracking.offer_name, tracking.provider]
      .filter(Boolean)
      .join(" — ");

  return (
    <div className="space-y-4">
      {showHeaderLabel && headerLabel && (
        <h3 className="text-sm font-medium text-muted">{headerLabel}</h3>
      )}

      <div
        className={cn(
          "rounded-lg border p-4 transition-colors",
          isDelivered
            ? "border-lime-500/30 bg-lime-500/5"
            : isError
              ? "border-red-500/30 bg-red-500/5"
              : "border-zinc-700 bg-zinc-800/30"
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div
              className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0",
                isDelivered
                  ? "bg-lime-500/20"
                  : isError
                    ? "bg-red-500/20"
                    : "bg-zinc-700"
              )}
            >
              <CheckCircle
                className={cn(
                  "h-5 w-5",
                  isDelivered
                    ? "text-lime-500"
                    : isError
                      ? "text-red-500"
                      : "text-zinc-400"
                )}
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-colorText truncate">
                {tracking.processing_status_label || tracking.processing_status}
              </h3>
              {tracking.delivered_at && (
                <p className="text-sm text-muted mt-0.5">{tracking.delivered_at}</p>
              )}
            </div>
          </div>
          {renderProviderBadge()}
        </div>
      </div>

      <section className="grid gap-6 grid-cols-1 lg:grid-cols-2 text-sm">
        <article className="space-y-1">
          <p className="text-muted text-sm">{t("orders:tracking.carrier")}</p>
          <span className="text-colorText text-sm font-medium">
            {tracking.carrier_label || tracking.carrier || "—"}
          </span>
        </article>

        <article className="space-y-1">
          <p className="text-muted text-sm">{t("orders:tracking.tracking_code")}</p>
          {tracking.tracking_code ? (
            <div className="flex items-center gap-2">
              <code className="bg-zinc-800 px-3 py-1.5 rounded-md text-primary font-mono text-sm border border-zinc-700">
                {tracking.tracking_code}
              </code>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 hover:bg-zinc-800"
                onClick={handleCopyCode}
                title={t("orders:tracking.copy_code")}
              >
                <Copy className="w-4 h-4" />
              </Button>
              {tracking.tracking_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 hover:bg-zinc-800"
                  onClick={() => window.open(tracking.tracking_url!, "_blank")}
                  title={t("orders:tracking.open_tracking")}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              )}
            </div>
          ) : (
            <span className="text-muted text-sm">—</span>
          )}
        </article>
      </section>
    </div>
  );
}

export default function TrackingSection({ tracking, trackings }: TrackingSectionProps) {
  const { t } = useTranslation(["orders"]);

  const items =
    trackings && trackings.length > 0
      ? trackings
      : tracking
        ? [tracking]
        : [];

  if (items.length === 0) {
    return null;
  }

  const multiple = items.length > 1;

  return (
    <div className="md:space-y-6 space-y-4">
      <div className="flex items-center gap-2">
        <TruckCircle />
        <h2 className="text-colorText font-semibold">
          {multiple
            ? t("orders:tracking.title_multiple", { count: items.length })
            : t("orders:tracking.title")}
        </h2>
      </div>

      <div className={cn(multiple ? "space-y-8" : "space-y-4")}>
        {items.map((item, index) => (
          <TrackingCard
            key={item.id || `${item.provider}-${index}`}
            tracking={item}
            showHeaderLabel={multiple}
          />
        ))}
      </div>
    </div>
  );
}
